import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { adminApi, type PaymentStatus } from '@/api/admin'
import { useTopBar } from '@/contexts/TopBarContext'
import { ChevronUp, ChevronDown, ChevronsUpDown, Search, X, Download, Upload, Check, Loader2 } from 'lucide-react'
import { usePagination } from '@/hooks/usePagination'
import { TablePagination } from '@/components/shared/TablePagination'
import { formatMoney } from '@/lib/money'
import { useAdminDates } from '@/hooks/useDates'

async function bulkDownload(ids: string[]) {
  const res = await adminApi.bulkDownloadInvoicePdf(ids)
  const url = URL.createObjectURL(new Blob([res.data as BlobPart], { type: 'application/zip' }))
  const a = document.createElement('a')
  a.href = url
  a.download = 'invoices.zip'
  a.click()
  URL.revokeObjectURL(url)
}

const PAYMENT_STATUSES: PaymentStatus[] = ['Unpaid', 'PartiallyPaid', 'Paid', 'Void']

const paymentLabel: Record<PaymentStatus, string> = {
  Unpaid: 'Unpaid', PartiallyPaid: 'Partially paid', Paid: 'Paid', Void: 'Void',
}
const paymentColour: Record<PaymentStatus, string> = {
  Unpaid: 'text-red-700 bg-red-50 border-red-200',
  PartiallyPaid: 'text-yellow-700 bg-yellow-50 border-yellow-200',
  Paid: 'text-green-700 bg-green-50 border-green-200',
  Void: 'text-gray-500 bg-gray-100 border-gray-200',
}

type SortKey = 'invoiceNumber' | 'issuedDate' | 'dueDate' | 'clientName' | 'courseName' | 'amount' | 'paymentStatus'
type SortDir = 'asc' | 'desc'

function SortIcon({ col, sortKey, dir }: { col: SortKey; sortKey: SortKey; dir: SortDir }) {
  if (col !== sortKey) return <ChevronsUpDown size={13} className="text-gray-300 ml-1 inline" />
  return dir === 'asc'
    ? <ChevronUp size={13} className="text-indigo-500 ml-1 inline" />
    : <ChevronDown size={13} className="text-indigo-500 ml-1 inline" />
}

function Th({ label, col, sortKey, dir, onSort }: {
  label: string; col: SortKey; sortKey: SortKey; dir: SortDir
  onSort: (col: SortKey) => void
}) {
  return (
    <th
      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 whitespace-nowrap"
      onClick={() => onSort(col)}
    >
      {label}<SortIcon col={col} sortKey={sortKey} dir={dir} />
    </th>
  )
}

export default function InvoicesListPage() {
  const { formatDate } = useAdminDates()
  const fmt = (date: string) => {
    return formatDate(date, { day: 'numeric', month: 'short', year: 'numeric' })
  }
  useTopBar(<span className="text-sm font-medium text-gray-900">Invoices</span>)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['all-invoices'],
    queryFn: () => adminApi.getAllInvoices().then(r => r.data),
  })

  const { data: xeroStatus } = useQuery({
    queryKey: ['xero-status'],
    queryFn: () => adminApi.getXeroStatus().then(r => r.data),
  })
  const xeroConnected = xeroStatus?.isConnected ?? false

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<PaymentStatus | ''>('')
  const [sortKey, setSortKey] = useState<SortKey>('issuedDate')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [downloading, setDownloading] = useState(false)
  const [bulkResult, setBulkResult] = useState<import('@/api/admin').BulkPushResult | null>(null)

  const pushOne = useMutation({
    mutationFn: (id: string) => adminApi.pushInvoiceToXero(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['all-invoices'] }),
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } }; message?: string }
      alert(err?.response?.data?.detail ?? err?.message ?? 'Push to Xero failed')
    },
  })

  const bulkPush = useMutation({
    mutationFn: (ids: string[]) => adminApi.bulkPushInvoicesToXero(ids).then(r => r.data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['all-invoices'] })
      setBulkResult(res)
      setSelected(new Set())
    },
  })

  const handleSort = (col: SortKey) => {
    if (sortKey === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(col); setSortDir('desc') }
  }

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim()
    return invoices
      .filter(inv => !statusFilter || inv.paymentStatus === statusFilter)
      .filter(inv => !q || [inv.invoiceNumber, inv.clientName, inv.courseSummary]
        .some(v => v.toLowerCase().includes(q)))
      .sort((a, b) => {
        let cmp = 0
        switch (sortKey) {
          case 'invoiceNumber': cmp = a.invoiceNumber.localeCompare(b.invoiceNumber); break
          case 'issuedDate':    cmp = a.issuedDate.localeCompare(b.issuedDate); break
          case 'dueDate':       cmp = (a.dueDate ?? '').localeCompare(b.dueDate ?? ''); break
          case 'clientName':    cmp = a.clientName.localeCompare(b.clientName); break
          case 'courseName':    cmp = a.courseSummary.localeCompare(b.courseSummary); break
          case 'amount':        cmp = a.amount - b.amount; break
          case 'paymentStatus': cmp = a.paymentStatus.localeCompare(b.paymentStatus); break
        }
        return sortDir === 'asc' ? cmp : -cmp
      })
  }, [invoices, search, statusFilter, sortKey, sortDir])

  const { paged, page, setPage, totalPages, total, pageSize, start } = usePagination(filtered)

  const allPageSelected = paged.length > 0 && paged.every(inv => selected.has(inv.id))
  const someSelected = selected.size > 0

  const toggleOne = (id: string) =>
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })

  const togglePage = () =>
    setSelected(prev => {
      const n = new Set(prev)
      if (allPageSelected) paged.forEach(inv => n.delete(inv.id))
      else paged.forEach(inv => n.add(inv.id))
      return n
    })

  const handleBulkDownload = async () => {
    setDownloading(true)
    try { await bulkDownload([...selected]) }
    finally { setDownloading(false) }
  }

  // Selected invoices not yet synced to Xero — the ones a bulk push would actually send.
  const unsyncedSelected = useMemo(
    () => invoices.filter(inv => selected.has(inv.id) && !inv.xeroSyncedAt).map(inv => inv.id),
    [invoices, selected],
  )

  const handleBulkPush = () => {
    if (unsyncedSelected.length > 0) bulkPush.mutate(unsyncedSelected)
  }

  const colCount = 8 + (xeroConnected ? 1 : 0)

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="text-sm text-gray-400">{filtered.length} of {invoices.length}</div>
        {someSelected && (
          <div className="flex items-center gap-2">
            {xeroConnected && unsyncedSelected.length > 0 && (
              <button
                onClick={handleBulkPush}
                disabled={bulkPush.isPending}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-indigo-200 text-indigo-700 bg-white hover:bg-indigo-50 disabled:opacity-60 transition-colors cursor-pointer"
              >
                {bulkPush.isPending
                  ? <><Loader2 size={14} className="animate-spin" /> Pushing…</>
                  : <><Upload size={14} /> Push {unsyncedSelected.length} to Xero</>}
              </button>
            )}
            <button
              onClick={handleBulkDownload}
              disabled={downloading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 transition-colors cursor-pointer"
            >
              <Download size={14} />
              {downloading ? 'Preparing…' : `Download ${selected.size} PDF${selected.size === 1 ? '' : 's'}`}
            </button>
          </div>
        )}
      </div>

      {/* Bulk push result */}
      {bulkResult && (
        <div className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
          <div>
            <span className="font-medium">Pushed {bulkResult.succeeded} to Xero</span>
            {bulkResult.skipped > 0 && <span> · {bulkResult.skipped} already synced</span>}
            {bulkResult.failed > 0 && <span className="text-red-700"> · {bulkResult.failed} failed</span>}
            {bulkResult.errors.length > 0 && (
              <ul className="mt-1 list-inside list-disc text-xs text-red-700">
                {bulkResult.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
                {bulkResult.errors.length > 5 && <li>…and {bulkResult.errors.length - 5} more</li>}
              </ul>
            )}
          </div>
          <button onClick={() => setBulkResult(null)} className="text-indigo-400 hover:text-indigo-600 cursor-pointer shrink-0">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        {/* Search */}
        <div className="relative flex-1 min-w-56">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search invoice #, client, course…"
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
              <X size={14} />
            </button>
          )}
        </div>

        {/* Status filter pills */}
        <div className="flex gap-1.5 flex-wrap">
          <button
            onClick={() => setStatusFilter('')}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border cursor-pointer transition-colors ${
              statusFilter === '' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
            }`}
          >
            All
          </button>
          {PAYMENT_STATUSES.map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(statusFilter === s ? '' : s)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border cursor-pointer transition-colors ${
                statusFilter === s ? paymentColour[s] + ' font-semibold' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
              }`}
            >
              {paymentLabel[s]}
            </button>
          ))}
        </div>
      </div>

      {/* Mobile card list */}
      <div className="sm:hidden space-y-2">
        {isLoading && <p className="text-center text-gray-500 py-8">Loading…</p>}
        {!isLoading && filtered.length === 0 && (
          <p className="text-center text-gray-400 py-8">
            {invoices.length === 0 ? 'No invoices yet.' : 'No invoices match your filters.'}
          </p>
        )}
        {paged.map(inv => (
          <div
            key={inv.id}
            onClick={() => navigate(`/admin/invoices/${inv.id}`)}
            className="bg-white border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-indigo-200"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="font-mono text-xs font-medium text-gray-500">{inv.invoiceNumber}</p>
                <p className="font-medium text-gray-900 mt-0.5">{inv.clientName}</p>
                <p className="text-xs text-gray-500 mt-0.5 truncate">{inv.courseSummary}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-medium text-gray-900">{formatMoney(inv.amount, inv.currency)}</p>
                <span className={`inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium border ${paymentColour[inv.paymentStatus]}`}>
                  {paymentLabel[inv.paymentStatus]}
                </span>
              </div>
            </div>
            <div className="mt-2 flex gap-x-4 text-xs text-gray-500">
              <span>{fmt(inv.issuedDate)}</span>
              {inv.dueDate && <span>Due: {fmt(inv.dueDate)}</span>}
            </div>
            {xeroConnected && (
              <div className="mt-2" onClick={e => e.stopPropagation()}>
                {inv.xeroSyncedAt ? (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                    <Check size={12} /> Synced to Xero
                  </span>
                ) : (
                  <button
                    onClick={() => pushOne.mutate(inv.id)}
                    disabled={pushOne.isPending && pushOne.variables === inv.id}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md border border-indigo-200 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 cursor-pointer"
                  >
                    {pushOne.isPending && pushOne.variables === inv.id
                      ? <><Loader2 size={12} className="animate-spin" /> Pushing…</>
                      : <><Upload size={12} /> Push to Xero</>}
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
        <TablePagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} start={start} onPage={setPage} />
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 w-8" onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    onChange={togglePage}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                  />
                </th>
                <Th label="Invoice #"   col="invoiceNumber" sortKey={sortKey} dir={sortDir} onSort={handleSort} />
                <Th label="Issued"      col="issuedDate"    sortKey={sortKey} dir={sortDir} onSort={handleSort} />
                <Th label="Due"         col="dueDate"       sortKey={sortKey} dir={sortDir} onSort={handleSort} />
                <Th label="Client"      col="clientName"    sortKey={sortKey} dir={sortDir} onSort={handleSort} />
                <Th label="Course"      col="courseName"    sortKey={sortKey} dir={sortDir} onSort={handleSort} />
                <Th label="Amount"      col="amount"        sortKey={sortKey} dir={sortDir} onSort={handleSort} />
                <Th label="Status"      col="paymentStatus" sortKey={sortKey} dir={sortDir} onSort={handleSort} />
                {xeroConnected && (
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Xero</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading && (
                <tr><td colSpan={colCount} className="text-center text-gray-500 py-12">Loading…</td></tr>
              )}
              {!isLoading && filtered.length === 0 && (
                <tr><td colSpan={colCount} className="text-center text-gray-400 py-12">
                  {invoices.length === 0 ? 'No invoices yet.' : 'No invoices match your filters.'}
                </td></tr>
              )}
              {paged.map(inv => (
                <tr
                  key={inv.id}
                  className={`cursor-pointer transition-colors ${selected.has(inv.id) ? 'bg-indigo-50' : 'hover:bg-indigo-50/40'}`}
                  onClick={() => navigate(`/admin/invoices/${inv.id}`)}
                >
                  <td className="px-4 py-3 w-8" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(inv.id)}
                      onChange={() => toggleOne(inv.id)}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-700 font-medium">{inv.invoiceNumber}</td>
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{fmt(inv.issuedDate)}</td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{inv.dueDate ? fmt(inv.dueDate) : '—'}</td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/clients/${inv.clientId}`}
                      className="font-medium text-gray-900 hover:text-indigo-600 hover:underline"
                      onClick={e => e.stopPropagation()}
                    >
                      {inv.clientName}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {inv.courseSummary}
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900">{formatMoney(inv.amount, inv.currency)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${paymentColour[inv.paymentStatus]}`}>
                      {paymentLabel[inv.paymentStatus]}
                    </span>
                  </td>
                  {xeroConnected && (
                    <td className="px-4 py-3 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                      {inv.xeroSyncedAt ? (
                        <span
                          title={`Synced ${fmt(inv.xeroSyncedAt)}`}
                          className="inline-flex items-center gap-1 text-xs font-medium text-green-700"
                        >
                          <Check size={13} /> Synced
                        </span>
                      ) : (
                        <button
                          onClick={() => pushOne.mutate(inv.id)}
                          disabled={pushOne.isPending && pushOne.variables === inv.id}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md border border-indigo-200 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 cursor-pointer"
                        >
                          {pushOne.isPending && pushOne.variables === inv.id
                            ? <><Loader2 size={12} className="animate-spin" /> Pushing…</>
                            : <><Upload size={12} /> Push</>}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TablePagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} start={start} onPage={setPage} />
      </div>
    </div>
  )
}
